import { useEffect, useState } from 'react';
import { Breakpoint, breakpoints } from '../../styles/breakpoints';
import debounce, { DebouncedFunc } from 'lodash-es/debounce';

export type Output = {
  size: Breakpoint;
  width: number;
  dimensions: {
    height: number;
    width: number;
  };
};

export function useResize(ms: number = 1000): Output {
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth
  });

  const [size, setSize] = useState<Breakpoint>(Breakpoint.xl);
  const [width, setWidth] = useState<number>(breakpoints.xl);

  const onResize = debounce((e: ResizeObserverEntry[]) => {
    return setDimensions({
      height: e[0]?.contentRect.height ?? 0,
      width: e[0]?.contentRect.width ?? 0
    });
  }, ms);

  // biome-ignore lint/correctness/useExhaustiveDependencies: exclude onResize to avoid infinite loop
  useEffect(() => {
    const resizeObserver = new window.ResizeObserver(onResize);
    resizeObserver?.observe(window.document.body);

    return () => {
      resizeObserver?.disconnect?.();
      (onResize as DebouncedFunc<ResizeObserverCallback>).cancel?.();
    };
  }, []);

  useEffect(() => {
    if (dimensions.width <= breakpoints.sm) {
      setSize(Breakpoint.sm);
      setWidth(breakpoints.sm);
    } else if (dimensions.width <= breakpoints.md) {
      setSize(Breakpoint.md);
      setWidth(breakpoints.md);
    } else if (dimensions.width <= breakpoints.lg) {
      setSize(Breakpoint.lg);
      setWidth(breakpoints.lg);
    } else {
      setSize(Breakpoint.xl);
      setWidth(breakpoints.xl);
    }
  }, [dimensions]);

  return { size, width, dimensions };
}
